import {
  applyColorPropertiesToItems,
  getColorByFamilyAndIndex,
} from '@/shared/utils/chartColors';
import { useMemo } from 'react';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';

interface ChartDataItem {
  name: string;
  value: number;
  colorFamily: string;
  colorIndex: number;
}

interface RateplanBreakdownItem {
  label: string;
  count: number;
}

interface SpotlightBreakdownChartProps {
  rateplanBreakdown: RateplanBreakdownItem[];
  defaultLabel?: string;
}

export const SpotlightBreakdownChart = ({
  rateplanBreakdown,
  defaultLabel,
}: SpotlightBreakdownChartProps) => {
  // Helper function to capitalize first letter only
  const capitalizeFirstLetter = (str: string) => {
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  };

  // Memoize chart data for spotlight breakdown
  const spotlightChartData = useMemo((): ChartDataItem[] => {
    if (!rateplanBreakdown) return [];

    const chartInput = rateplanBreakdown.map((item) => ({
      name: item.label,
      value: item.count,
      colorFamily: item.label === defaultLabel ? 'blue' : 'successful',
    }));

    return applyColorPropertiesToItems(
      chartInput,
      (item) => item.colorFamily
    ) as ChartDataItem[];
  }, [rateplanBreakdown, defaultLabel]);

  // Custom tooltip for spotlight chart
  const SpotlightChartTooltip = ({
    active,
    payload,
  }: {
    active?: boolean;
    payload?: Array<{ payload: ChartDataItem }>;
  }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-base-100 rounded p-2 shadow-md">
          <p className="text-sm font-medium">{capitalizeFirstLetter(data.name)}</p>
          <p className="text-base-content/70 text-sm">
            {data.value.toLocaleString()} contacts
          </p>
        </div>
      );
    }
    return null;
  };

  if (spotlightChartData.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-col items-center md:w-1/3">
      <div className="aspect-square w-full max-w-[200px]">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={spotlightChartData}
              innerRadius={'50%'}
              dataKey="value"
              nameKey="name"
              labelLine={false}>
              {spotlightChartData.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={getColorByFamilyAndIndex(entry.colorFamily, entry.colorIndex)}
                />
              ))}
            </Pie>
            <Tooltip content={<SpotlightChartTooltip />} />
          </PieChart>
        </ResponsiveContainer>
      </div>
      {/* Legend */}
      <div className="mt-3 flex flex-col gap-2">
        {spotlightChartData.map((entry) => (
          <div key={entry.name} className="flex items-center gap-2">
            <div
              className="h-3 w-3 rounded-full"
              style={{
                backgroundColor: getColorByFamilyAndIndex(
                  entry.colorFamily,
                  entry.colorIndex
                ),
              }}></div>
            <span className="text-xs text-gray-600">
              {capitalizeFirstLetter(entry.name)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};
